// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskType, useEvolveDelete } from '@evolvemep/api-ts';
import { Checkbox, Flex, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { GridMenu } from 'components/Mantine/GridMenu';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';

import { CreateEditTaskTypeModal } from './CreateEditTaskType/CreateEditTaskTypeModal';
import { useTaskTypes } from './useTaskTypes';

export const TaskTypeListItem = ({
  task,
  onTaskSelect,
  hideDelete,
  isWorkCellMapping,
}: {
  task: TaskType;
  onTaskSelect: (task: TaskType) => void;
  hideDelete?: boolean;
  isWorkCellMapping?: boolean;
}) => {
  const { selectedGroups, selectedTasks, groupsPaginatedGet, hideSelect } = useTaskTypes();

  const { reset: refreshGroupList } = groupsPaginatedGet;

  const { apiCall: deleteTask, loading: deleting } = useEvolveDelete(`shop/taskType/${task.taskTypeId}`);

  const disabled = isWorkCellMapping && task.isMappedToWorkCell;

  return (
    <Flex
      align="center"
      h={70}
      justify="space-between"
      mx="md"
      my="sm"
      onClick={!disabled ? () => onTaskSelect(task) : undefined}
    >
      <Flex gap="sm" align="center" justify="left" flex={1}>
        {!hideSelect && (
          <Checkbox
            disabled={disabled}
            checked={
              selectedTasks.some((t) => t.taskTypeId === task.taskTypeId) ||
              selectedGroups.some((g) => g.taskTypeGroupId === task.taskTypeGroupId)
            }
          />
        )}
        <Flex gap="sm" flex={1}>
          {isNotNil(task.image) && (
            <ImageFromFile
              fileId={task.image.fileId}
              alt={task.name}
              size={80}
              withContainer
              fallback={<EvolveIcon icon="WorkCellTask" color="gray" />}
            />
          )}
          <Stack
            fz="sm"
            gap="xs"
            w="100%"
            justify="center"
            pr="lg"
            style={{
              userSelect: 'none',
            }}
          >
            <Flex gap="sm" align="center">
              <Text lineClamp={1}>{task.name}</Text>
              {isWorkCellMapping && task.isMappedToWorkCell && (
                <AgGridStyleTooltip label="Mapped to a work cell">
                  <EvolveIcon icon="Pin" color="primary" size="sm" />
                </AgGridStyleTooltip>
              )}
            </Flex>
            {task.description && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {task.description}
              </Text>
            )}
          </Stack>
        </Flex>
      </Flex>
      <Flex align="center">
        <AttachmentsPopover taskTypeId={task.taskTypeId} numberOfAttachments={task.attachmentCount} />
        <GridMenu options={['Edit', { value: 'Delete', hidden: hideDelete }]}>
          {(selectedMenuOption, resetMenu) => (
            <>
              <WrappedModal
                title="Delete task"
                opened={selectedMenuOption === 'Delete'}
                onClose={() => resetMenu()}
                loading={deleting}
                onConfirm={() => deleteTask().then(() => refreshGroupList())}
                confirmationText="Delete"
              >
                <Text>
                  Are you sure want to delete <b>{task.name}</b>?
                </Text>
                <Text>Deleting a task will remove it from every catalog assembly in which it appears.</Text>
              </WrappedModal>
              <CreateEditTaskTypeModal
                taskType={selectedMenuOption === 'Edit' ? task : undefined}
                modalOnly
                onClose={resetMenu}
              />
            </>
          )}
        </GridMenu>
      </Flex>
    </Flex>
  );
};
